import React, {FC, useCallback, useMemo, useRef, useState} from "react";
import Axios from "axios";
import {
    ActionButton,
    ChoiceGroup,
    DefaultButton,
    IChoiceGroupOption,
    Stack,
    Text,
    TextField
} from "@fluentui/react";
import {SharedColors} from "@fluentui/theme";
import {
    AresDeploymentEnvironment,
    AresDeploymentGroupSnapshotSummaryDto,
    PrepareAresDeploymentGroupSnapshotsBatchResponseDto
} from "../API";
import {GetApiError} from "./ShowApiError";
import {LoadingButton} from "./LoadingButton";
import {OnixCallout} from "./OnixCallout";

type TCalloutToggle = (
    e: React.MouseEvent<any, any> | undefined,
    data: any,
    forceStatus?: boolean,
    renderData?: any,
) => void;

export type AresDeploymentBatchPrepareGroup = {
    id?: number;
    name?: string;
};

interface IAresDeploymentBatchPrepareSnapshotCalloutProps {
    disabled?: boolean;
    groups: AresDeploymentBatchPrepareGroup[];
    keyData?: string;
    onPrepared?: () => void | Promise<void>;
}

const targetEnvironmentOptions: IChoiceGroupOption[] = [
    {key: `${AresDeploymentEnvironment.Testing}`, text: "Testing"},
    {key: `${AresDeploymentEnvironment.Staging}`, text: "Staging"},
    {key: `${AresDeploymentEnvironment.Production}`, text: "Production"},
];

const safeRun = async (callback?: () => void | Promise<void>) => {
    try {
        await callback?.();
    } catch {
    }
};

const getSnapshotLabel = (snapshot?: AresDeploymentGroupSnapshotSummaryDto) => {
    if (!snapshot) {
        return "";
    }

    return snapshot.name || (snapshot.versionNo ? `v${snapshot.versionNo}` : "");
};

export const AresDeploymentBatchPrepareSnapshotCallout: FC<IAresDeploymentBatchPrepareSnapshotCalloutProps> = ({
    disabled,
    groups,
    keyData,
    onPrepared,
}) => {
    const [running, setRunning] = useState(false);
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [targetEnvironment, setTargetEnvironment] = useState<AresDeploymentEnvironment>(
        AresDeploymentEnvironment.Testing
    );
    const [error, setError] = useState<string>();
    const [response, setResponse] = useState<PrepareAresDeploymentGroupSnapshotsBatchResponseDto>();
    const toggleRef = useRef<TCalloutToggle>();

    const selectedGroups = useMemo(() => {
        const unique = new Map<number, AresDeploymentBatchPrepareGroup>();
        for (const group of groups ?? []) {
            if (group?.id) {
                unique.set(group.id, group);
            }
        }

        return [...unique.values()];
    }, [groups]);

    const reset = useCallback(() => {
        setName("");
        setNotes("");
        setTargetEnvironment(AresDeploymentEnvironment.Testing);
        setError(undefined);
        setResponse(undefined);
    }, []);

    const dismiss = useCallback(() => {
        if (!running) {
            reset();
            toggleRef.current?.(undefined, undefined, false);
        }
    }, [reset, running]);

    const runWorkflow = useCallback(async () => {
        if (running || selectedGroups.length < 2) {
            return;
        }

        setRunning(true);
        setError(undefined);
        setResponse(undefined);

        try {
            const {data} = await Axios.post<PrepareAresDeploymentGroupSnapshotsBatchResponseDto>(
                "/AresDeploymentGroupSnapshot/PrepareBatch",
                {
                    groupIds: selectedGroups.map(v => v.id),
                    name: name.trim() || undefined,
                    notes: notes.trim() || undefined,
                    targetEnvironment,
                }
            );

            setResponse(data ?? {
                requestedGroupCount: selectedGroups.length,
                testRunCount: 0,
                items: [],
                hasErrors: false,
            });
            await safeRun(onPrepared);
        } catch (e) {
            setError(GetApiError(e) ?? "Nepoznata greška.");
        } finally {
            setRunning(false);
        }
    }, [name, notes, onPrepared, running, selectedGroups, targetEnvironment]);

    return (
        <OnixCallout
            keyData={keyData}
            titleless
            heightless
            minWidth={500}
            customMaxWidth={640}
            onDismiss={() => {
                if (!running) {
                    reset();
                }
            }}
            trigger={(id, show) => {
                toggleRef.current = show;

                return (
                    <ActionButton
                        id={id}
                        disabled={disabled || running || selectedGroups.length < 2}
                        iconProps={{iconName: "Rocket"}}
                        onClick={(event) => show(event, undefined)}
                    >
                        {running
                            ? "Pripremam snapshote..."
                            : `Pripremi snapshote (${selectedGroups.length})`}
                    </ActionButton>
                );
            }}
        >
            {(realign) => (
                <Stack tokens={{childrenGap: 10, padding: 12}} style={{maxWidth: 590}}>
                    <Stack tokens={{childrenGap: 4}}>
                        <Text variant={"mediumPlus"}>
                            {`Pripremi snapshot za ${selectedGroups.length} grupa`}
                        </Text>
                        <Text variant={"small"}>
                            Ako naziv ostane prazan, svaka grupa dobiva svoj sljedeći vX.
                        </Text>
                    </Stack>

                    <Stack tokens={{childrenGap: 3}}>
                        {selectedGroups.map(group => (
                            <Text key={group.id} variant={"small"}>
                                {group.name || `Grupa ${group.id}`}
                            </Text>
                        ))}
                    </Stack>

                    <ChoiceGroup
                        label={"Snapshot postavi kao"}
                        options={targetEnvironmentOptions}
                        selectedKey={`${targetEnvironment}`}
                        disabled={running}
                        onChange={(_, option) => {
                            if (!option) {
                                return;
                            }
                            setTargetEnvironment(Number(option.key) as AresDeploymentEnvironment);
                            setError(undefined);
                            setResponse(undefined);
                        }}
                    />

                    <TextField
                        label={"Naziv verzije"}
                        value={name}
                        placeholder={"svaka grupa koristi svoj sljedeći vX"}
                        disabled={running}
                        onFocus={realign}
                        onChange={(_, value) => {
                            setName(value ?? "");
                            setError(undefined);
                            setResponse(undefined);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                void runWorkflow();
                            }
                        }}
                    />

                    <TextField
                        label={"Opis verzije"}
                        value={notes}
                        multiline
                        rows={4}
                        disabled={running}
                        onFocus={realign}
                        onChange={(_, value) => {
                            setNotes(value ?? "");
                            setError(undefined);
                            setResponse(undefined);
                        }}
                    />

                    {error ? (
                        <Text role={"alert"} styles={{root: {color: SharedColors.red20}}}>
                            {error}
                        </Text>
                    ) : null}

                    {response ? (
                        <Stack tokens={{childrenGap: 6}}>
                            <Text role={"status"} styles={{root: {color: response.hasErrors ? SharedColors.red20 : SharedColors.green20}}}>
                                {`Gotovo. Testova pokrenuto: ${response.testRunCount ?? 0}`}
                            </Text>
                            {(response.items ?? []).map(item => {
                                const succeeded = item.status === "Succeeded";
                                const snapshotLabel = getSnapshotLabel(item.snapshot);

                                return (
                                    <Stack
                                        key={`${item.groupId}-${item.status}-${snapshotLabel}`}
                                        tokens={{childrenGap: 2}}
                                        styles={{
                                            root: {
                                                borderTop: "1px solid #edebe9",
                                                paddingTop: 6,
                                            }
                                        }}
                                    >
                                        <Text variant={"small"}>
                                            <b>{item.groupName || `Grupa ${item.groupId}`}</b>
                                            {snapshotLabel ? ` - ${snapshotLabel}` : ""}
                                        </Text>
                                        <Text
                                            variant={"small"}
                                            styles={{root: {color: succeeded ? SharedColors.green20 : SharedColors.red20}}}
                                        >
                                            {succeeded ? "snapshot pripremljen" : "greška"}
                                            {item.testReused ? " - testovi ponovljeni" : " - testovi pokrenuti"}
                                        </Text>
                                        {item.message ? <Text variant={"small"}>{item.message}</Text> : null}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    ) : null}

                    <Stack horizontal horizontalAlign={"end"} tokens={{childrenGap: 8}}>
                        <DefaultButton disabled={running} text={response ? "Gotovo" : "Odustani"} onClick={dismiss}/>
                        <LoadingButton
                            loading={running}
                            disabled={running || selectedGroups.length < 2}
                            text={running ? "Radim..." : "Pokreni sve"}
                            onClick={() => void runWorkflow()}
                        />
                    </Stack>
                </Stack>
            )}
        </OnixCallout>
    );
};
